import { Typography } from '@/ui/atoms/typography/Typography'
import { ColumnDefinition, MobileViewOptions } from '@/ui/molecules/data-table/types'
import { ResponsiveDataTable } from '@/ui/organisms/responsive-data-table/ResponsiveDataTable'
import { useMemo } from 'react'
import { ProcessedActivity, TransactionAction, getActionDisplayName } from '../logic/useActivity'
import { ActionsCell } from '@/ui/molecules/data-table/components/ActionsCell'
import { LinkButton } from '@/ui/atoms/button/Button'
import { cn } from '@/ui/utils/style'
import { useBreakpoint } from '@/ui/utils/useBreakpoint'
import { useChains } from 'wagmi'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { BaseUnitNumber, NormalizedUnitNumber } from '@/domain/types/NumericValues'
import Link from 'next/link'
import { useEulerIndexerELends } from '@/domain/euler/elends/elendsQuery'
import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { getVaultMarket } from '@/features/evault-details/data/products'
import { isAddress, type Address } from 'viem'

interface Props {
  activities?: ProcessedActivity[]
}

function ActivityDisplay({
  activity,
  mobileViewOptions,
}: {
  activity: ProcessedActivity
  mobileViewOptions?: MobileViewOptions
}) {
  const assetToken = activity.assetToken
  const collateralToken = activity.collateralToken

  const amount = activity.assetToken?.format(
    NormalizedUnitNumber(activity.assetToken?.fromBaseUnit(BaseUnitNumber(activity.amount ?? '0'))),
    {
      style: 'compact',
    },
  )

  const collateralAmount = activity.collateralToken?.format(
    NormalizedUnitNumber(activity.collateralToken?.fromBaseUnit(BaseUnitNumber(activity.collateralAmount ?? '0'))),
    {
      style: 'compact',
    },
  )

  const TokenComponent = (
    <div className="flex flex-col gap-1">
      <div className={cn('flex items-center gap-2')}>
        <div className="flex shrink-0">
          {((activity.action === TransactionAction.LIQUIDATION && collateralToken) ||
            (activity.action === TransactionAction.SWAP && collateralToken)) && (
            <TokenIcon token={collateralToken} className="h-6 w-6" />
          )}

          {activity.action === TransactionAction.DEPOSIT_COLLATERAL ||
          activity.action === TransactionAction.WITHDRAW_COLLATERAL
            ? collateralToken && <TokenIcon token={collateralToken} className="h-6 w-6" />
            : assetToken && <TokenIcon token={assetToken} className="h-6 w-6" />}
        </div>
        <div className="flex flex-col items-start">
          <div
            className={cn(
              'font-semibold text-base capitalize',
              activity.action === TransactionAction.LIQUIDATION ? 'text-red-500' : 'text-foreground',
            )}
          >
            {getActionDisplayName(activity.action)}
          </div>
          <div className="flex items-center gap-2">
            <div className="font-normal text-muted-foreground text-sm">
              {(activity.action === TransactionAction.LIQUIDATION && collateralAmount) ||
              (activity.action === TransactionAction.SWAP && collateralAmount)
                ? `${collateralAmount} ${collateralToken?.symbol} for ${amount} ${assetToken?.symbol}`
                : activity.action === TransactionAction.DEPOSIT_COLLATERAL ||
                    activity.action === TransactionAction.WITHDRAW_COLLATERAL
                  ? `${collateralAmount} ${collateralToken?.symbol}`
                  : `${amount} ${assetToken?.symbol}`}
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  if (mobileViewOptions?.isMobileView) {
    return (
      <div className={cn('flex flex-col gap-1', mobileViewOptions.showOnMobile ? 'items-center' : 'items-start')}>
        {!mobileViewOptions.showOnMobile && (
          <Typography variant="prompt" className="w-full">
            {mobileViewOptions.rowTitle}
          </Typography>
        )}
        {TokenComponent}
      </div>
    )
  }

  return TokenComponent
}

export function ActivityTable({ activities = [] }: Props) {
  const desktop = useBreakpoint('md')
  const chains = useChains()
  const { data: indexerVaults } = useEulerIndexerELends(E_LEND_CHAIN_ID)

  const columnDefinition = useMemo<{ [key: string]: ColumnDefinition<ProcessedActivity> }>(
    () => ({
      activity: {
        header: 'Activity',
        headerAlign: 'left',
        renderCell: (activity, mobileViewOptions) => {
          return <ActivityDisplay activity={activity} mobileViewOptions={mobileViewOptions} />
        },
      },
      market: {
        header: 'Market',
        headerAlign: 'center',
        sortable: true,
        sortingFn: (a, b) => a.original.type.localeCompare(b.original.type),
        renderCell: (
          { type, assetToken, collateralToken, vaultName, vaultAddress, product, chainId: activityChainId },
          mobileViewOptions,
        ) => {
          // Euler activities: show vault name with link to the correct detail page
          // HypurrFi Earn vaults → /lend/{chainId}/{address}, others → /markets/elend/{chainId}/{address}
          if (type === 'euler' && vaultName) {
            const sanitizedVaultName = vaultName.slice(0, 100)
            const sanitizedProduct = product?.slice(0, 50)

            let vaultLink: string | undefined
            if (vaultAddress && isAddress(vaultAddress)) {
              const market = getVaultMarket(vaultAddress as Address, indexerVaults?.get(vaultAddress as Address)?.products)
              // Use the activity's chainId (always E_LEND_CHAIN_ID for Euler), not the wallet's chain
              vaultLink =
                market?.key === 'hypurrfi-earn'
                  ? `/lend/${activityChainId}/${vaultAddress}`
                  : `/markets/elend/${activityChainId}/${vaultAddress}`
            }

            const label = (
              <div className="flex flex-col items-center">
                {vaultLink ? (
                  <Link
                    href={vaultLink}
                    className="text-center text-foreground text-sm hover:text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {sanitizedVaultName}
                  </Link>
                ) : (
                  <p className="text-center text-foreground text-sm">{sanitizedVaultName}</p>
                )}
                {sanitizedProduct && (
                  <p className="whitespace-nowrap text-muted-foreground text-xs">{sanitizedProduct}</p>
                )}
              </div>
            )

            if (mobileViewOptions?.isMobileView) {
              const mobileLabel = (
                <div className="flex flex-col items-end">
                  {vaultLink ? (
                    <Link
                      href={vaultLink}
                      className="text-right text-foreground text-sm hover:text-primary hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {sanitizedVaultName}
                    </Link>
                  ) : (
                    <p className="text-right text-foreground text-sm">{sanitizedVaultName}</p>
                  )}
                  {sanitizedProduct && (
                    <p className="whitespace-nowrap text-muted-foreground text-xs">{sanitizedProduct}</p>
                  )}
                </div>
              )

              return (
                <div
                  className={cn(
                    'flex flex-row items-center',
                    mobileViewOptions.showOnMobile ? 'justify-center' : 'justify-between',
                  )}
                >
                  {!mobileViewOptions.showOnMobile && (
                    <Typography variant="prompt" className="w-full">
                      {mobileViewOptions.rowTitle}
                    </Typography>
                  )}
                  {mobileLabel}
                </div>
              )
            }

            return label
          }

          // Non-Euler activities: original rendering
          if (mobileViewOptions?.isMobileView) {
            return (
              <div
                className={cn(
                  'flex flex-row items-center',
                  mobileViewOptions.showOnMobile ? 'justify-center' : 'justify-between',
                )}
              >
                {!mobileViewOptions.showOnMobile && (
                  <Typography variant="prompt" className="w-full">
                    {mobileViewOptions.rowTitle}
                  </Typography>
                )}
                <p className="text-foreground text-sm capitalize">{type}</p>
              </div>
            )
          }

          return (
            <div className="flex flex-col items-center justify-center">
              <p className="text-center text-foreground text-sm capitalize">{type}</p>
              {type === 'isolated' && (
                <p className="text-muted-foreground text-xs">
                  {collateralToken?.symbol} / {assetToken?.symbol}
                </p>
              )}
            </div>
          )
        },
      },
      date: {
        header: 'Date',
        headerAlign: 'right',
        sortable: true,
        sortingFn: (a, b) => {
          const aTime = new Date(a.original.timestamp).getTime()
          const bTime = new Date(b.original.timestamp).getTime()
          return bTime - aTime // Most recent first
        },
        renderCell: (activity, mobileViewOptions) => {
          // Split the date into time and date parts using the original timestamp
          const dateObj = new Date(activity.timestamp)
          const timeString = dateObj.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
          })
          const dateString = dateObj.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })

          if (mobileViewOptions?.isMobileView) {
            return (
              <div
                className={cn(
                  'flex flex-row items-center',
                  mobileViewOptions.showOnMobile ? 'justify-center' : 'justify-between',
                )}
              >
                {!mobileViewOptions.showOnMobile && (
                  <Typography variant="prompt" className="w-full">
                    {mobileViewOptions.rowTitle}
                  </Typography>
                )}
                <div className="text-right">
                  <p className="whitespace-nowrap text-foreground text-sm">{dateString}</p>
                  <p className="text-muted-foreground text-xs">{timeString}</p>
                </div>
              </div>
            )
          }

          return (
            <div className="text-right">
              <p className="text-foreground text-sm">{dateString}</p>
              <p className="text-muted-foreground text-xs">{timeString}</p>
            </div>
          )
        },
      },
      ...(!desktop
        ? {
            actions: {
              header: '',
              renderCell: ({ chainId, transactionHash }) => {
                const chain = chains.find((chain) => chain.id === chainId)
                const blockExplorerLink = chain?.blockExplorers?.default.url

                return (
                  <ActionsCell className="lg:hidden">
                    <LinkButton
                      className="w-full"
                      size="sm"
                      href={`${blockExplorerLink}/tx/${transactionHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View transaction
                    </LinkButton>
                  </ActionsCell>
                )
              },
            },
          }
        : {}),
    }),
    [desktop, chains, indexerVaults],
  )

  const tableDataFormatted = useMemo(() => {
    if (!activities) return []

    return activities.map((row) => {
      const chain = chains.find((chain) => chain.id === row.chainId)
      const blockExplorerLink = chain?.blockExplorers?.default.url

      return {
        ...row,
        rowClickOptions: desktop
          ? {
              destination: `${blockExplorerLink}/tx/${row.transactionHash}`,
              external: true,
            }
          : undefined,
      }
    })
  }, [activities, desktop, chains])

  if (!activities?.length) {
    return (
      <div className="flex items-center justify-center">
        <Typography variant="h3" className="my-12 text-center" gradient>
          No activity found.
        </Typography>
      </div>
    )
  }

  return (
    <ResponsiveDataTable
      gridTemplateColumnsClassName="grid-cols-[repeat(4,_1fr)] md:grid-cols-[_3fr_1fr_2fr]"
      columnDefinition={columnDefinition}
      data={tableDataFormatted || []}
      variant="default"
      withPanel
    />
  )
}
